// Which side of the ledger a balance sits on, and how to say so.
//
// WHY A SIGNED NUMBER IS NOT ENOUGH
//
// Every balance in this system is stored signed in the account's NATURAL direction: positive
// means "the normal way round for this kind of account". So 5,000 on Cash at Bank is a debit
// and 5,000 on a landlord's payable is a credit — the same number, opposite meanings, and
// nothing on screen said which.
//
// That is readable only by someone who already knows the account's type and whether it is a
// contra. Anyone else — and anyone in a hurry — sees a column of numbers that cannot be
// checked against a statement, because a statement says Dr or Cr.
//
// THE RULE
//
// Assets and expenses are debit accounts; liabilities, equity and income are credit accounts.
// A CONTRA account carries the opposite balance to its type, which is the whole point of
// being one: 1900 Accumulated Depreciation is an asset account holding a credit, and 5900 is
// an expense account holding a credit.
//
// A negative balance then means the account is standing the other way round — an overdrawn
// bank account is a credit, a landlord who has been overpaid is a debit. Both are worth
// seeing at a glance, and both were previously just a minus sign the reader had to interpret.
//
// The same rule lives in backend/utils/openingBalances.js as naturalSideIsDebit, which
// decides which side an opening balance POSTS to. If one changes, so must the other, or the
// screen will describe a balance differently from the way the ledger wrote it.

/** Which side a POSITIVE balance sits on, for this kind of account. */
export function naturalSideIsDebit(account = {}) {
  const typeIsDebit = account.type === 'asset' || account.type === 'expense';
  return account.isContraAccount ? !typeIsDebit : typeIsDebit;
}

/**
 * Which side a given balance actually sits on.
 *
 * @returns {'debit'|'credit'|null}  null for a nil balance, which has no side
 */
export function balanceSide(amount, account = {}) {
  const value = Number(amount);
  if (!Number.isFinite(value) || Math.abs(value) < 0.005) return null;
  const natural = naturalSideIsDebit(account);
  // A negative balance is the account standing the other way round.
  return (value > 0) === natural ? 'debit' : 'credit';
}

/** 'Dr' / 'Cr' / '' — the abbreviation an accountant reads. */
export function sideLabel(amount, account = {}) {
  const side = balanceSide(amount, account);
  return side === 'debit' ? 'Dr' : side === 'credit' ? 'Cr' : '';
}

/**
 * The magnitude of a balance. Paired with sideLabel, never shown alone: the sign is carried
 * by Dr/Cr, so printing it as well would say the same thing twice and read as a negative
 * debit rather than a credit.
 */
export function balanceMagnitude(amount) {
  const value = Number(amount);
  return Number.isFinite(value) ? Math.abs(value) : 0;
}

/**
 * What a POSITIVE figure typed into an opening balance box will do.
 *
 * The form takes a plain number and setOpeningBalance puts it on the account's natural side,
 * so somebody entering 5,000,000 against Accumulated Depreciation is entering a CREDIT
 * whether they meant to or not. Saying so beside the field is the difference between that
 * being a choice and a surprise.
 */
export function openingBalanceHint(account = {}) {
  if (!account.type) return 'Choose a type first — it decides whether this is a debit or a credit.';
  const side = naturalSideIsDebit(account) ? 'DEBIT' : 'CREDIT';
  const opposite = naturalSideIsDebit(account) ? 'credit' : 'debit';
  return `A positive figure is a ${side} on this account. Enter a negative one for a ${opposite}.`;
}
